import esriConfig from "@arcgis/core/config";
import WebTileLayer from "@arcgis/core/layers/WebTileLayer";
import WMSLayer from "@arcgis/core/layers/WMSLayer";
import Extent from "@arcgis/core/geometry/Extent";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer";
import MapImageLayer from "@arcgis/core/layers/MapImageLayer";
import GeoJSONLayer from "@arcgis/core/layers/GeoJSONLayer";
import { LayerType } from "../../types/layer-type";
import {
  applyFeatureChange,
  arcgisBaseURL,
  changeDetailsObjectFromArcgisAsyncAction,
  clearSelectedGraphic,
  extentEstateEn,
  focusOnFeature,
  getLayers,
  highlightSelectedObject,
  initializeMap,
  polylineOff,
  polylineOn,
  sendChanges,
  setConvertedFeatures,
  takeScreenshot,
  unfocusOnFeature,
  zoomIn,
  zoomOut,
} from "../actions/map";
import {
  changeRotationAngle,
  hiddenMapDataTypes,
  MapSection,
  saveScreenshot,
  setAttributesModalSelectedFeature,
  setLayersVisible,
  setLoading,
  setMapComponentReady,
  validArcgisLayerTypes,
} from "../reducers/map";
import createViewer, {
  AddCityBoundaryLayer,
  applyHoverEffect,
  finishDrawingProcess,
  getPopUpTemplate,
  startDrawingProcess,
  stopMeasuring,
  createScreenshot,
  handleFocusOnFeature,
  unFocus,
  handleMapFeatureClick,
  highlightObject,
  waitForViewerReady,
  CreateHybridBaseMap,
  AddLegend,
  changeBaseMap,
  handleExtentEstate,
} from "../../utils/helpers-for-2d-map";
import { ChangeType } from "../../interfaces/change-data";
import { setError } from "../reducers/upload-file";
import { LayersDataTypes } from "../../types/layers-data-types";
import mapPinIcon from "../../images/map-pin.svg";
import PictureMarkerSymbol from "@arcgis/core/symbols/PictureMarkerSymbol";
import PopupTemplate from "@arcgis/core/PopupTemplate";
import { ArcgisHybridLayer } from "../../interfaces/layer";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import {
  addConvertedFeaturesToLayer,
  clearLastSelectedGraphic,
  handleFeatureClickStyle,
} from "../../utils/master-map-helper";

export const arcgisMiddleware = (section) => {
  return (store) => {
    let loadedObjectsLayer;
    let viewer = null;
    let baseMap;
    const { dispatch } = store;
    function addLayers(viewer) {
      const { layers, visibleLayersId } = store.getState().map;

      const layersIds = layers.map((layer) => layer.name);

      viewer.map.layers
        .filter((layer) => !layersIds.includes(layer.id))
        .forEach((layer) => viewer.map.remove(layer));

      layers
        .filter((layer) => !hiddenMapDataTypes.includes(layer.dataType))
        .forEach((layer) => {
          const layerId = layer.name;

          if (viewer.map.findLayerById(layerId)) {
            return;
          }

          const alwaysVisibleLayer =
            layer.dataType === LayersDataTypes.OmanBorder;
          const visible =
            visibleLayersId.includes(layer.id) ||
            visibleLayersId.includes(layer.parentId) ||
            alwaysVisibleLayer;

          if (layer.dataType === LayersDataTypes.BaseMap) {
            if (visible) {
              changeBaseMap(layer, viewer, getBaseMap);
            }
          } else if (layer.type === LayerType.ArcgisFeatureServer) {
            const layerProperties = {
              title: layer.label,
              id: layer.name,
              url: layer.url,
              visible: visible,
              editingEnabled: layer.canEdit ?? false,
              definitionExpression: layer.style2D?.query,
              outFields: ["*"],
              ...(layer.style2D?.label?.type === "onclick" &&
                layer.style2D?.label && {
                  popupTemplate: new PopupTemplate(
                    getPopUpTemplate(layer.style2D?.label),
                  ),
                }),
            };
            if (layer.style2D?.default) {
              layerProperties.renderer = layer.style2D?.default;
            }
            let arcgisLayer;
            if (layer.dataType === LayersDataTypes.CityBoundary) {
              arcgisLayer = AddCityBoundaryLayer(
                viewer,
                layerProperties,
                layer,
                pictureMarkerSymbol,
              );
            } else {
              arcgisLayer = new FeatureLayer(layerProperties);

              let hoverStyle = layer.style2D?.hover;
              const isHover = layer.style2D?.label?.type === "hover";
              const mode =
                (hoverStyle ? "style" : "") + (isHover ? "popup" : "");

              if (mode) {
                if (isHover && layer.style2D?.label) {
                  arcgisLayer.popupTemplate = new PopupTemplate(
                    getPopUpTemplate(layer.style2D?.label),
                  );
                }
                applyHoverEffect(
                  viewer,
                  arcgisLayer,
                  mode,
                  hoverStyle,
                  layer.style2D?.fieldName,
                );
              }
              if (
                [
                  LayersDataTypes.Plots,
                  LayersDataTypes.InvestmentOpportunities,
                ].includes(layer.dataType)
              ) {
                AddLegend(viewer, arcgisLayer);
              }

              viewer.map.add(arcgisLayer);
            }

            arcgisLayer.load().then(() => {
              arcgisLayer.on("edits", (event) => {
                logEdit(event, layer.id);
              });
            });
          } else if (layer.type === LayerType.TMS) {
            let wLayer = new WebTileLayer({
              title: layer.label,
              id: layer.name,
              visible: visible,
              fullExtent: Extent.fromJSON(layer.style2D?.extents),
              urlTemplate: `${layer.url}/{z}/{x}/{y}.png`,
              getTileUrl: function (level, row, col) {
                return this.urlTemplate
                  .replace("{z}", level)
                  .replace("{x}", col)
                  .replace(
                    "{y}",
                    layer.style2D?.reverseY
                      ? -1 * row + Math.pow(2, level) - 1
                      : row,
                  );
              },
            });

            wLayer.load();

            viewer.map.add(wLayer);
          } else if (layer.type === LayerType.WMS) {
            let wmsLayer = new WMSLayer({
              title: layer.label,
              id: layer.name,
              url: layer.url,
              visible: visible,
            });
            if (layer.style2D?.subLayer) {
              wmsLayer.load().then(() => {
                const sublayer = wmsLayer.findSublayerByName(
                  layer.style2D.subLayer,
                );
                if (sublayer) {
                  wmsLayer.sublayers = [sublayer];
                }
              });
            } else {
              wmsLayer.load();
            }
            viewer.map.add(wmsLayer);
          } else if (layer.type === LayerType.ArcgisMapServer) {
            const mapImageLayer = new MapImageLayer({
              title: layer.label,
              id: layer.name,
              url: layer.url,
              visible: visible,
            });
            if (layer.style2D?.subLayers) {
              mapImageLayer.load().then(() => {
                mapImageLayer.allSublayers.forEach((subLayer) => {
                  subLayer.visible = layer.style2D.subLayers.includes(
                    subLayer.id,
                  );
                });
              });
            }
            viewer.map.add(mapImageLayer);
          } else if (layer.type === LayerType.GeoJSON) {
            const layerProperties = {
              title: layer.label,
              id: layer.name,
              url: layer.url,
              visible: visible,
              outFields: ["*"],
            };
            if (layer.style2D?.default) {
              layerProperties.renderer = layer.style2D?.default;
            }
            const geoJSONLayer = new GeoJSONLayer(layerProperties);

            if (layer.dataType === LayersDataTypes.Reports) {
              let hoverStyle = layer.style2D?.hover;
              const isHover = layer.style2D?.label?.type === "hover";
              const mode =
                (hoverStyle ? "style" : "") + (isHover ? "popup" : "");
              if (mode) {
                if (isHover && layer.style2D?.label) {
                  geoJSONLayer.popupTemplate = new PopupTemplate(
                    getPopUpTemplate(layer.style2D?.label),
                  );
                }
                applyHoverEffect(
                  viewer,
                  geoJSONLayer,
                  mode,
                  hoverStyle,
                  layer.style2D?.fieldName,
                );
              }
              AddLegend(viewer, geoJSONLayer);
            }
            viewer.map.add(geoJSONLayer);
          }
        });
    }
    function getBaseMap(layer) {
      return layer === ArcgisHybridLayer ? baseMap : null;
    }
    function updateLayerVisible(layer) {
      const visibleLayersId = store.getState().map.visibleLayersId;
      const alwaysVisibleLayer = layer.dataType === LayersDataTypes.OmanBorder;

      if (layer.dataType === LayersDataTypes.BaseMap) {
        if (visibleLayersId.includes(layer.id))
          changeBaseMap(layer, viewer, getBaseMap);
      } else if (validArcgisLayerTypes.includes(layer.type)) {
        let mapLayers = viewer.map.layers.filter((l) => l.id === layer.name);
        if (mapLayers) {
          if (mapLayers) {
            const visible =
              visibleLayersId.includes(layer.id) ||
              (!!layer.parentId && visibleLayersId.includes(layer.parentId)) ||
              alwaysVisibleLayer;

            if (!visible && viewer?.popup?.visible) {
              const currentPopupFeatures = viewer.popup?.features ?? [];

              const isPopupFromTargetLayer = currentPopupFeatures.some(
                (feature) => feature?.layer?.id === layer.name,
              );

              if (isPopupFromTargetLayer) {
                viewer.popup.close();
              }
            }

            Promise.all(
              mapLayers.map((mapLayer) => {
                mapLayer.visible = visible;
                return mapLayer.load();
              }),
            ).finally(() => {
              dispatch(setLoading({ id: layer.id, loading: false }));
            });
          }
        }
      }
    }
    function logEdit(event, layerId) {
      if (!event.editedFeatures) return;
      const features = event.editedFeatures[0].editedFeatures;
      let changes = null;
      let changeType;
      if (event.addedFeatures.length) {
        changes = event.addedFeatures.map((item, index) => {
          let feature = features.adds[index];
          return {
            objectId: item.objectId,
            attributes: JSON.stringify(feature.attributes),
            geometry: JSON.stringify(feature.geometry),
          };
        });
        changeType = ChangeType.add;
      } else if (event.deletedFeatures.length) {
        changes = event.deletedFeatures.map((item, index) => {
          let feature = features.deletes[index];
          return {
            objectId: item.objectId,
            attributes: JSON.stringify(feature.attributes),
            geometry: JSON.stringify(feature.geometry),
          };
        });
        changeType = ChangeType.remove;
      } else if (event.updatedFeatures.length) {
        changes = event.updatedFeatures.map((item, index) => {
          let feature = features.updates[index].original;
          return {
            objectId: item.objectId,
            attributes: JSON.stringify(feature.attributes),
            geometry: JSON.stringify(feature.geometry),
          };
        });
        changeType = ChangeType.edit;
      } else return;
      dispatch(
        sendChanges({
          layerId: layerId,
          changeType,
          changes,
        }),
      );
    }
    const pictureMarkerSymbol = new PictureMarkerSymbol({
      url: mapPinIcon,
      width: "36px",
      height: "36px",
      yoffset: "16px",
    });

    function clearSelection() {
      clearLastSelectedGraphic(loadedObjectsLayer);

      if (viewer.popup.visible) {
        viewer.popup.close();
      }

      dispatch(setAttributesModalSelectedFeature(null));
    }

    return (next) => async (action) => {
      if (initializeMap.match(action)) {
        if (
          action.payload.section === section &&
          action.payload.mode === "2d"
        ) {
          if (viewer === null) {
            await next(action);

            baseMap = CreateHybridBaseMap();
            viewer = createViewer(
              store,
              addLayers,
              `${section}MapContainer`,
              baseMap,
            );

            viewer.on("click", async function (event) {
              const response = await viewer.hitTest(event);

              const clickedGraphicResult = response.results[0];

              handleMapFeatureClick(event, viewer, store, dispatch);

              const visibleEditAttributesModal =
                store.getState().map.attributesModal.visible;

              if (clickedGraphicResult && "graphic" in clickedGraphicResult) {
                const graphic = clickedGraphicResult.graphic;

                handleFeatureClickStyle(
                  graphic,
                  store,
                  loadedObjectsLayer,
                  viewer,
                );

                if (
                  clickedGraphicResult.graphic.layer instanceof GraphicsLayer
                ) {
                  dispatch(
                    setAttributesModalSelectedFeature({
                      layerName: graphic.layerName,
                      attributes: graphic.attributes,
                    }),
                  );
                } else {
                  if (visibleEditAttributesModal) {
                    clearSelection();
                  }
                }
              } else {
                clearSelection();
              }
            });

            viewer.container.addEventListener("keydown", (event) => {
              if (event.key === "Escape") {
                clearSelection();
                return;
              }
            });

            await waitForViewerReady(viewer);
            dispatch(setMapComponentReady(section));
          }
          return;
        } else if (viewer !== null) {
          unFocus();
          viewer.map?.removeAll();
          viewer?.destroy();
          viewer = null;
          dispatch(setMapComponentReady(MapSection.none));
        }
      }
      if (viewer === null || store.getState().map.section !== section) {
        return next(action);
      }

      if (takeScreenshot.match(action)) {
        const { dataUrl } = await createScreenshot(
          viewer,
          action.payload,
          getBaseMap,
          false,
          true,
        );
        dispatch(
          saveScreenshot({
            name: action.payload.name,
            dataUrl,
          }),
        );
      }

      if (focusOnFeature.match(action)) {
        let { layer, geometry, objectId } = action.payload;
        handleFocusOnFeature(
          layer,
          geometry,
          objectId,
          dispatch,
          store,
          viewer,
        );
      }

      if (extentEstateEn.match(action)) {
        const selectedCity = action.payload;
        handleExtentEstate(selectedCity, store, viewer);
      }

      if (highlightSelectedObject.match(action)) {
        let { objectId, layer } = action.payload;
        highlightObject(objectId, layer, viewer);
      }

      if (unfocusOnFeature.match(action)) {
        unFocus();
      }

      if (polylineOff.match(action)) {
        finishDrawingProcess(viewer);
        stopMeasuring(viewer);
      }
      if (polylineOn.match(action)) {
        startDrawingProcess(viewer);
      }
      if (setLayersVisible.match(action)) {
        next(action);
        store.getState().map.layers.forEach(updateLayerVisible);
        return;
      }
      if (changeRotationAngle.match(action)) {
        next(action);
        viewer.rotation = action.payload;
        return;
      }
      if (zoomIn.match(action)) {
        viewer.zoom += 1;
      }
      if (zoomOut.match(action)) {
        viewer.zoom -= 1;
      }
      if (getLayers.fulfilled.match(action)) {
        next(action);
        let token = store.getState().map.arcgisToken;
        if (token) {
          esriConfig.request.interceptors.push({
            before: function (params) {
              if (!params.url.startsWith(arcgisBaseURL)) return;
              params.requestOptions.query = params.requestOptions.query || {};
              params.requestOptions.query.token = token.token;
            },
          });
        }
        addLayers(viewer);
        viewer.map.basemap.load();
        return;
      }

      if (applyFeatureChange.match(action)) {
        let { layer, feature, changeType } = action.payload;

        let mapLayer = viewer.map.layers.find((l) => l.id === layer.name);

        if (!mapLayer) {
          dispatch(setError(`Layer ${layer.name} not found`));
          return;
        }

        const featuresWithGeometry = feature.map((feat) => {
          if (!feat || !feat.geometry) {
            dispatch(setError("Feature or geometry not found."));
            return null;
          }
          return feat;
        });

        mapLayer
          .applyEdits(
            changeType === ChangeType.add
              ? { addFeatures: featuresWithGeometry }
              : changeType === ChangeType.edit
                ? { updateFeatures: featuresWithGeometry }
                : changeType === ChangeType.delete
                  ? {
                      deleteFeatures: featuresWithGeometry.map((f) => ({
                        objectId: f.objectId,
                      })),
                    }
                  : {},
            { returnServiceEditsOption: "original-and-current-features" },
          )
          .then((result) => {
            if (result?.error) {
              const errorMessage =
                result.error.message || "Unknown error occurred";
              const errorDetails = result.error.details?.join(", ") || "";
              dispatch(
                setError(
                  `Error applying edits: ${errorMessage}. Details: ${errorDetails}`,
                ),
              );
              throw new Error(
                `Server error: ${errorMessage}. Details: ${errorDetails}`,
              );
            }
          })
          .catch((error) => {
            const errorMessage =
              error.details?.messages[0] ||
              error.message ||
              "Unknown error occurred";
            dispatch(setError(`Error applying edits: ${errorMessage}`));
            return Promise.resolve();
          });
      }

      if (changeDetailsObjectFromArcgisAsyncAction.match(action)) {
        let mapLayer = viewer.map.findLayerById(action.payload.layer.name);

        mapLayer
          .queryFeatures({
            where: `${mapLayer.objectIdField} = ${action.payload.objectId}`,
            returnGeometry: true,
            outFields: ["*"],
          })
          .then((result) => {
            if (result.features.length > 0) {
              let feature = result.features[0];

              const keysToUpdate = Object.keys(action.payload.newAttributes);

              keysToUpdate.forEach((key) => {
                if (feature.attributes.hasOwnProperty(key)) {
                  feature.attributes[key] = action.payload.newAttributes[key];
                }
              });

              mapLayer
                .applyEdits({
                  updateFeatures: [feature],
                })
                .then(() => {
                  if (mapLayer && typeof mapLayer.refresh === "function") {
                    mapLayer.refresh();
                  }

                  action.payload.deferred.resolve();
                })
                .catch((error) => {
                  action.payload.deferred.reject(error);
                });
            }
          });
      }

      if (setConvertedFeatures.match(action)) {
        if (!loadedObjectsLayer) {
          loadedObjectsLayer = new GraphicsLayer();
          viewer.map.add(loadedObjectsLayer);
        }
        const convertedGeometry = store.getState().map.convertedGeometry;
        addConvertedFeaturesToLayer(convertedGeometry, loadedObjectsLayer);
        return next(action);
      }

      if (clearSelectedGraphic.match(action)) {
        clearSelection();
      }

      next(action);
    };
  };
};
